import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, ExternalLink, RefreshCw } from 'lucide-react';
import { copyTextToClipboard } from '../../utils/clipboard';
import { openExternal } from '../../utils/openExternal';
import { platformKey } from './terminalProfiles';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { problemMessage, profileProblem } from './profileProblem';

/**
 * "`codex` was not found on your PATH" + the install command for this OS
 * with Copy, a Docs link and Re-check. Inline (in the "+" menu, or over a
 * pane whose program could not start) — never a modal. We never run the
 * installer for the user.
 *
 * A command that is an app, a shortcut or a folder (card 6) gets that said
 * instead — no install command, which would not help.
 */
export default function ProfileInstallHint({ profile, onRecheck, rechecking, variant = 'menu' }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const detected = useTerminalConfigStore((s) => s.detectionProblems?.[profile.command]);
  const problem = profileProblem(profile.command, detected);
  const install = profile.install || {};
  const cmd = problem ? '' : install[platformKey()] || install.win || '';

  const copy = async () => {
    if (await copyTextToClipboard(cmd)) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className={`term-install term-install--${variant}`} role="group" aria-label={t('terminalExt.installTitle', 'Install {{name}}', { name: profile.name })}>
      <div className="term-install-why">
        {problem ? problemMessage(t, problem, profile.command) : t('terminalExt.notFoundOnPath', '{{command}} was not found on your PATH.', { command: profile.command })}
      </div>
      {cmd ? (
        <>
          <div className="term-install-label">{t('terminalExt.installWith', 'Install it with')}</div>
          <div className="term-install-cmd">
            <code>{cmd}</code>
            <button type="button" className="term-install-copy" onClick={copy} aria-live="polite">
              {copied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
              {copied ? t('terminalExt.copied', 'Copied') : t('terminalExt.copyInstall', 'Copy')}
            </button>
          </div>
          {install.alt && !problem && (
            <div className="term-install-alt">
              {t('terminalExt.installAlt', 'or')} <code>{install.alt}</code>
            </div>
          )}
        </>
      ) : null}
      <div className="term-install-actions">
        {install.docs && (
          <button type="button" className="term-install-link" onClick={() => openExternal(install.docs)}>
            <ExternalLink size={12} aria-hidden />
            {t('terminalExt.docs', 'Docs')}
          </button>
        )}
        {onRecheck && (
          <button type="button" className="term-install-link" onClick={onRecheck} disabled={rechecking}>
            <RefreshCw size={12} className={rechecking ? 'animate-spin' : ''} aria-hidden />
            {t('terminalExt.recheck', 'Re-check')}
          </button>
        )}
      </div>
    </div>
  );
}
